import { Button, EmptyState, Panel } from '@bcecloud/portal-ui';
import { periodLabel } from '@/lib/report-window';
import { PAGE_TRAFFIC, type PageTraffic, type PageTypesStats } from '@/lib/page-types';

/** Where paid data stands for a page-types response. */
export const PAID_COVERAGE = {
  /** All-traffic mode, or paid data covers the whole range. */
  full: 'full',
  /** Paid data starts partway through the range. */
  partial: 'partial',
  /** No paid sessions synced for any day in the range yet. */
  none: 'none',
} as const;
export type PaidCoverage = (typeof PAID_COVERAGE)[keyof typeof PAID_COVERAGE];

/**
 * How much of the range paid numbers cover. Pure, so the rule tests without
 * rendering.
 *
 * @param traffic - The active traffic mode.
 * @param stats - The page-types response for that mode.
 * @returns full / partial / none.
 */
export function paidCoverage(
  traffic: PageTraffic,
  stats: Pick<PageTypesStats, 'paidDataFrom' | 'range'>,
): PaidCoverage {
  if (traffic !== PAGE_TRAFFIC.paid) return PAID_COVERAGE.full;
  if (stats.paidDataFrom === null) return PAID_COVERAGE.none;
  return stats.paidDataFrom > stats.range.from ? PAID_COVERAGE.partial : PAID_COVERAGE.full;
}

/** One line under the filters when paid data starts partway through the range. */
export function PaidCoverageNote({ from }: { readonly from: string }): React.ReactElement {
  const day = periodLabel(from, 'day');
  return (
    <p className="-mt-2 mb-4 text-portal-micro text-portal-muted">
      Paid sessions start {day}, so paid numbers cover {day} onward. Google Ads leads from earlier days are left out.
    </p>
  );
}

/**
 * The empty state for paid mode on a range with no paid sessions synced yet:
 * says the gap is missing history, not zero paid traffic, and offers the way
 * back to all traffic.
 */
export function NoPaidDataState({ onShowAll }: { readonly onShowAll: () => void }): React.ReactElement {
  return (
    <Panel>
      <EmptyState
        title="No paid data for this range yet"
        message="Paid sessions are recorded from the first sync that pulled them, so this range has nothing to show yet. That is missing history, not zero paid traffic. Pick a more recent range, or switch to all traffic."
        action={
          <Button variant="secondary" onClick={onShowAll}>
            Show all traffic
          </Button>
        }
      />
    </Panel>
  );
}
